/**
 * React Query Hooks for Points System
 */

import { useQuery } from '@tanstack/react-query';
import {
  getSellerPoints,
  getSellerPointsDetailed,
  getMyPointsHistory,
  getPointsLeaderboard,
} from '../api/points';

/**
 * Hook to fetch seller's public points summary
 * @param {string} sellerId - Seller's user ID
 * @param {object} options - React Query options
 */
export function useSellerPoints(sellerId, options = {}) {
  return useQuery({
    queryKey: ['points', 'seller', sellerId],
    queryFn: () => getSellerPoints(sellerId),
    enabled: !!sellerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch detailed points breakdown
 * @param {string} sellerId - Seller's user ID
 * @param {object} options - React Query options
 */
export function useSellerPointsDetailed(sellerId, options = {}) {
  return useQuery({
    queryKey: ['points', 'seller', sellerId, 'detailed'],
    queryFn: () => getSellerPointsDetailed(sellerId),
    enabled: !!sellerId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to fetch authenticated seller's points history
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {object} options - React Query options
 */
export function useMyPointsHistory(page = 1, limit = 20, options = {}) {
  return useQuery({
    queryKey: ['points', 'me', 'history', page, limit],
    queryFn: () => getMyPointsHistory(page, limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook to fetch admin points leaderboard
 * @param {number} limit - Number of top sellers
 * @param {object} options - React Query options
 */
export function usePointsLeaderboard(limit = 50, options = {}) {
  return useQuery({
    queryKey: ['points', 'leaderboard', limit],
    queryFn: () => getPointsLeaderboard(limit),
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}
